<template>
  <div
    v-if="!isLoading2"
    class="section"
  >
    <div class="columns is-centered">
      <div class="column is-11">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              Family
            </h2>
            <div class="subtitle is-2">
              List the name and occupation of your spouse if living in your household or life partner as defined in Chapter 9-1100 of the Philadelphia Code and the name(s) and occupation(s) or profession(s) of your minor dependent children.
            </div>
            <checkbox-adv
              id="nothing-to-report"
              v-model="nothingToReport"
              :value="true"
            >
              Check this box if you have no <strong>Family</strong> to report.
            </checkbox-adv>
          </div>
        </div>

        <div
          v-if="!nothingToReport"
          class="section"
        >
          <validation-observer
            ref="form-family"
            tag="form"
          >
            <div class="columns is-multiline">
              <div class="column is-4">
                <dropdown-vee
                  v-model="newItem.type"
                  :options="familyRelationshipsDropdown"
                  placeholder="Relationship"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox-vee
                  id="family-member-name"
                  v-model="newItem.name"
                  placeholder="Family Member Name"
                  rules="required"
                  required
                />
              </div>
              <div class="column is-4">
                <textbox
                  id="family-member-occupation"
                  v-model="newItem.occupation"
                  placeholder="Family Member Occupation"
                />
              </div>
              <div class="column is-12 has-text-centered">
                <button
                  id="action-btn"
                  type="button"
                  class="button is-secondary"
                  @click="trigerNewItemAction"
                >
                  {{ tableAction }} Family Member
                </button>
              </div>
            </div>
          </validation-observer>
          <div
            v-if="items"
            class="columns"
          >
            <div class="column">
              <v-client-table
                ref="vc-table"
                :data="allItems"
                :columns="table"
                :options="tableOptions"
              >
                <div
                  slot="type"
                  slot-scope="props"
                >
                  {{ getControlValueDesc(props.row.type, 'familyRelationships') }}
                </div>
                <div
                  slot="edit"
                  slot-scope="props"
                >
                  <button
                    :id="`edit-btn-${props.row.id}`"
                    type="button"
                    title="Edit"
                    aria-label="Edit item"
                    class="button is-small is-warning"
                    @click.prevent="editItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-pencil-alt"
                    />
                  </button>
                </div>
                <div
                  slot="delete"
                  slot-scope="props"
                >
                  <button
                    :id="`delete-btn-${props.row.id}`"
                    title="Delete"
                    aria-label="Delete item"
                    class="button is-small is-danger"
                    @click.prevent="deleteItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-trash"
                    />
                  </button>
                </div>
                <div
                  slot="clone"
                  slot-scope="props"
                >
                  <button
                    :id="`clone-btn-${props.row.id}`"
                    type="button"
                    title="Clone"
                    aria-label="Clone item"
                    class="button is-small is-secondary"
                    @click.prevent="cloneItem(props.row.id)"
                  >
                    <i
                      aria-hidden="true"
                      class="fa fa-copy"
                    />
                  </button>
                </div>
              </v-client-table>
            </div>
          </div>
        </div>
        <validation-observer
          ref="form-family-certification"
          tag="form"
        >
          <validation-provider
            v-slot="{ errors, reset }"
            name="Certification"
            rules="required"
            class="validate-tooltip section"
            mode="eager"
          >
            <div class="columns validate-tooltip v-center-top v-arrow-center-bottom">
              <transition name="fade">
                <span
                  v-if="errors[0]"
                  class="column shrink error popover"
                  @click="reset()"
                  v-text="errors[0]"
                />
              </transition>
            </div>
            <fieldset>
              <legend class="subtitle">
                Choose one of the following Certifications:
              </legend>
              <div class="columns">
                <div class="column is-6">
                  <radio-adv
                    id="certA"
                    v-model="currentFiling.mayorCertChosenOnline"
                    name="certification"
                    value="A"
                  >
                    <span v-html="certificates ? certificates[0]['description'] : ''" />
                  </radio-adv>
                </div>
                <div class="column is-6">
                  <radio-adv
                    id="certB"
                    v-model="currentFiling.mayorCertChosenOnline"
                    name="certification"
                    value="B"
                  >
                    <span v-html="certificates ? certificates[1]['description'] : ''" />
                  </radio-adv>
                </div>
              </div>
            </fieldset>
          </validation-provider>
        </validation-observer>
        <div class="columns">
          <div class="column has-text-centered">
            <button
              id="save-btn"
              type="button"
              class="button is-primary"
              :class="isSaving ? 'is-loading' : ''"
              :disabled="isSaving"
              @click="familyCertficationCheck()"
            >
              Save and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { controlValueHelpers } from '@/mixins/controlValue';
import CheckboxAdv from '@/components/phila-ui/CheckboxAdv';
import RadioAdv from '@/components/phila-ui/RadioAdv';
import { Textbox, Dropdown } from '@phila/phila-ui';
import { withValidation } from 'vee-validate';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

Vue.use(ClientTable, {}, false, 'bulma');


export default {
  name: 'FamilyMembers',
  components: {
    CheckboxAdv,
    TextboxVee,
    RadioAdv,
    Textbox,
    DropdownVee,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  data() {
    return {
      currentFiling: this.getCurrentFiling(),
      items: this.getCurrentFamilyMembers(),
      isSaving: false,
      newCert: null,
      table: [
        'type',
        'name',
        'occupation',
        'edit',
        'clone',
        'delete',
      ],
      tableOptions: {
        columnsClasses: {
          'edit': 'actions has-text-centered',
          'delete': 'actions has-text-centered',
          'clone': 'actions has-text-centered',
        },
        pagination: {
          dropdown: false,
          edge: true,
        },
        sortable: [],
        perPage: 50,
        perPageValues: [],
        filterable: false,
        headings: {
        },
      },
      newItem: {
        type: '',
        name: '',
        occupation: '',
      },
      nothingToReport: false,
    };
  },
  computed: {
    familyRelationships () {
      return this.$store.getters[ 'admin/activeControlValues' ]('familymember');
    },
    familyRelationshipsDropdown () {
      return this.controlValueDropdown('familymember');
    },
    certificates () {
      return this.$store.getters[ 'admin/activeControlValues' ]('mayorformcert');
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.hasNothingToReport();
    this.$store.commit('loading', false);
  },
  methods: {
    async initialAssign () {
      this.currentFiling = this.getCurrentFiling();
      this.items = this.getCurrentFamilyMembers();
      this.hasNothingToReport();
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'familymember'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'mayorformcert'));
      promises.push(this.$store.dispatch('family/fetchFamilyMembers'));
      return Promise.all(promises);
    },
    hasNothingToReport () {
      if (this.items.length === 0 && this.currentFiling.familymembersCompleted === 'Y') {
        this.nothingToReport = true;
      }
    },
    getCurrentFamilyMembers () {
      return JSON.parse(JSON.stringify(this.$store.state.family.familyMembers));
    },
    saveAndContinue () {
      let promises = [];

      const col = 'familymembersCompleted';
      const endpoint = 'family-members';

      let filing = {
        id: this.currentFiling.id,
        mayorCertChosenOnline: this.currentFiling.mayorCertChosenOnline,
      };

      filing[col] = this.currentFiling[col];

      let isDeletingAll = this.isDeletingAllItems();

      if (this.nothingToReport) {
        filing[col] = 'Y';
      } else {
        if (this.items.length === 0) {
          filing[col] = 'N';
        } else {
          promises.push(this.$store.dispatch('filing/saveFilingScreen', {
            items: this.items,
            endpoint,
          }));
          if (isDeletingAll === true) {
            filing[col] = 'N';
          } else {
            filing[col] = 'Y';
          }

        }
      }

      let self = this;

      promises.push(this.$store.dispatch('filing/saveFiling', filing).then(() => {

        if (filing[col] === 'Y' && this.nothingToReport === true) {

          self.items.forEach(item => {
            self.deleteItem(item.id);
          }, self);

          self.$store.dispatch('filing/saveFilingScreen', {
            items: self.items,
            endpoint,
          });

        }
      }));


      Promise.all(promises).then(() => {
        this.$router.push({ name: this.nextFilingScreen });
      });
    },
    async familyCertficationCheck () {

      const itemRef = this.$refs['form-family-certification'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      this.saveAndContinueCheck('Family Members');

    },
    async trigerNewItemAction () {
      const itemRef = this.$refs['form-family'];
      const isValid = await itemRef.validate();

      if (!isValid) {
        return false;
      }

      if (this.tableAction === 'Add') {
        this.addNewItem();
      }
      if (this.tableAction === 'Update') {
        this.updateItem();
      }
      itemRef.reset();
    },
  },
};
</script>
